import { useEffect, useRef, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useAppStore } from '@shared/store'
import { SITE } from '@shared/config'
import { getAllPosts, categorySlug } from '@shared/lib/posts'
import { localeCategoryRoute, localeRoot, postLocaleFromPath, POST_LOCALE_LABELS } from '@shared/lib/post-locale'
import { LanguageSwitcher } from '@shared/ui/LanguageSwitcher'
import * as styles from './Header.css'

/** Categories with post counts, largest first — sets the nav menu order. */
function categoryCounts(locale: 'en' | 'fr' | 'es'): { name: string; count: number }[] {
  const counts = new Map<string, number>()
  for (const p of getAllPosts(locale)) {
    if (p.category) counts.set(p.category, (counts.get(p.category) ?? 0) + 1)
  }
  return Array.from(counts.entries())
    .map(([name, count]) => ({ name, count }))
    .sort((a, b) => b.count - a.count || a.name.localeCompare(b.name))
}

export function Header() {
  const navigate = useNavigate()
  const location = useLocation()
  const { isMenuOpen, toggleMenu } = useAppStore()
  const locale = postLocaleFromPath(location.pathname)
  const labels = POST_LOCALE_LABELS[locale]
  const [scrolled, setScrolled] = useState(false)
  const [hidden, setHidden] = useState(false)
  const [closing, setClosing] = useState(false)
  const lastY = useRef(0)

  const navLinks = [
    { label: labels.home, href: localeRoot(locale) },
    { label: labels.about, href: '/about' },
  ]

  const categories = categoryCounts(locale)

  useEffect(() => {
    const onScroll = () => {
      const y = window.scrollY
      setScrolled(y > 60)
      setHidden(y > lastY.current && y > 200)
      lastY.current = y
    }
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => window.removeEventListener('scroll', onScroll)
  }, [])

  const handleCloseMenu = () => {
    setClosing(true)
    setTimeout(() => { setClosing(false); toggleMenu() }, 330)
  }

  const handleNavClick = (href: string) => {
    if (isMenuOpen) handleCloseMenu()
    setTimeout(() => navigate(href), isMenuOpen ? 360 : 0)
  }

  const headerClass = [
    styles.header,
    scrolled ? styles.headerScrolled : styles.headerTop,
    hidden && !isMenuOpen ? styles.headerHidden : '',
  ].join(' ')

  return (
    <>
      <header className={headerClass}>
        <a
          href={localeRoot(locale)}
          className={styles.logo}
          onClick={(e) => {
            e.preventDefault()
            location.pathname === localeRoot(locale) ? window.scrollTo({ top: 0, behavior: 'smooth' }) : navigate(localeRoot(locale))
          }}
        >
          <img
            src="/images/logo_black.webp"
            alt={SITE.name}
            style={{ height: '54px', width: 'auto', display: 'block' }}
          />
        </a>

        <nav className={styles.nav}>
          {navLinks.map((link) => (
            <a
              key={link.label}
              href={link.href}
              className={styles.navLink}
              onClick={(e) => { e.preventDefault(); handleNavClick(link.href) }}
            >
              {link.label}
            </a>
          ))}
        </nav>

        <div className={styles.actions}>
          <LanguageSwitcher />
          <button className={styles.hamburger} onClick={isMenuOpen ? handleCloseMenu : toggleMenu} aria-label="Toggle menu">
            <div className={styles.hamburgerInner}>
              <span className={[styles.hamburgerLine, styles.hamburgerLine1, isMenuOpen ? styles.hamburgerLine1Open : ''].join(' ')} />
              <span className={[styles.hamburgerLine, styles.hamburgerLine2, isMenuOpen ? styles.hamburgerLine2Open : ''].join(' ')} />
              <span className={[styles.hamburgerLine, styles.hamburgerLine3, isMenuOpen ? styles.hamburgerLine3Open : ''].join(' ')} />
            </div>
          </button>
        </div>
      </header>

      {(isMenuOpen || closing) && (
        <nav className={`${styles.mobileMenu} ${closing ? styles.mobileMenuClosing : ''}`}>
          <div className={styles.menuInner}>
            <section className={styles.menuSection}>
                <span className={styles.menuLabel}>{labels.menu}</span>
              <ul className={styles.primaryList}>
                {navLinks.map((link, i) => (
                  <li key={link.label} className={styles.menuItem} style={{ animationDelay: `${i * 50}ms` }}>
                    <a
                      href={link.href}
                      className={styles.primaryLink}
                      onClick={(e) => { e.preventDefault(); handleNavClick(link.href) }}
                    >
                      {link.label}
                    </a>
                  </li>
                ))}
              </ul>
            </section>

            <section className={styles.menuSection}>
                <span className={styles.menuLabel}>{labels.categories}</span>
              <ul className={styles.categoryList}>
                {categories.map((cat, i) => (
                  <li key={cat.name} className={styles.menuItem} style={{ animationDelay: `${(navLinks.length + i) * 50}ms` }}>
                    <a
                      href={localeCategoryRoute(locale, categorySlug(cat.name))}
                      className={styles.categoryLink}
                      onClick={(e) => { e.preventDefault(); handleNavClick(localeCategoryRoute(locale, categorySlug(cat.name))) }}
                    >
                      <span>{cat.name}</span>
                      <span className={styles.categoryCount}>{cat.count}</span>
                    </a>
                  </li>
                ))}
              </ul>
            </section>
          </div>

          <div className={styles.mobileMenuBottom}>
            <span className={styles.mobileMenuBottomText}>{SITE.name} © {new Date().getFullYear()}</span>
            <a
              href="/privacy-policy"
              className={styles.mobileMenuBottomLink}
              onClick={(e) => { e.preventDefault(); handleNavClick('/privacy-policy') }}
            >
              {labels.privacy}
            </a>
          </div>
        </nav>
      )}
    </>
  )
}
